import { ProblemDetails, ProblemDetailsError, toProblemDetails } from "./ApiHelpers.ts"; // Adjust path
import { ErrorContext, FetchError, Middleware, ResponseContext, ResponseError } from "./gen/runtime.ts"; // Assuming runtime is in 'gen'

export const problemDetailsMiddleware: Middleware = {
	async post(context: ResponseContext): Promise<Response | void> {
		if (!context.response.ok) {
			// HTTP status is 200-299
			// It's an error response, try to parse as ProblemDetails
			// Clone the response because its body can only be consumed once.
			// toProblemDetails should handle cloning if it needs to read the body.
			const problem = await toProblemDetails(context.response.clone());
			if (problem) {
				throw new ProblemDetailsError(problem);
			}
			// If it's not a ProblemDetails, we don't throw here.
			// BaseAPI.request will throw a generic ResponseError later if we don't.
			// Or, you could choose to throw a generic ResponseError here as well:
			// throw new ResponseError(context.response, `HTTP error ${context.response.status} was not a ProblemDetails object.`);
		}
		// If response is ok, or if it was an error but not ProblemDetails,
		// return undefined (or context.response) to allow normal processing.
		// The original response from fetch will be passed along.
		return undefined;
	},

	async onError(context: ErrorContext): Promise<Response | void> {
		// This onError will now primarily handle:
		// 1. FetchErrors (network issues, CORS, etc.) from the raw fetch call.
		// 2. Errors explicitly thrown by `pre` middleware.
		// 3. Errors explicitly thrown by other `post` middleware (though less common to re-process).
		// 4. If toProblemDetails in the `post` handler above throws an unexpected error.

		// If a ProblemDetailsError was already created (e.g., by our post-middleware or another one)
		if (context.error instanceof ProblemDetailsError) {
			throw context.error;
		}

		// If it's a ResponseError (e.g., thrown by another middleware, less likely now for status codes)
		if (context.error instanceof ResponseError) {
			const problem = await toProblemDetails(context.error.response.clone());
			if (problem) {
				throw new ProblemDetailsError(problem);
			}
			throw context.error; // Re-throw original ResponseError if not ProblemDetails
		}

		// Handle FetchError (network errors, etc.)
		if (context.error instanceof FetchError) {
			// You might want to convert this to a standardized ProblemDetails format too
			const networkProblem: ProblemDetails = {
				type: "urn:ietf:rfc:7231#section-6.6.1", // Generic "Internal Server Error" type or custom
				title: "Network Error",
				status: 0, // No HTTP status for pure network errors, or use a convention like 503
				detail: `A network error occurred while trying to reach ${context.url}: ${context.error.cause?.message || context.error.message}`
			};
			throw new ProblemDetailsError(networkProblem);
		}

		// For other types of errors that are instances of Error
		if (context.error instanceof Error) {
			// Potentially wrap this in a generic ProblemDetails too
			const unknownProblem: ProblemDetails = {
				type: "urn:ietf:rfc:7231#section-6.6.1",
				title: "An Unexpected Error Occurred",
				status: 500,
				detail: `An unexpected error occurred during the API request to ${context.url}: ${context.error.message}`
			};
			throw new ProblemDetailsError(unknownProblem);
		}

		// Fallback for unknown error types (not instances of Error)
		const fallbackProblem: ProblemDetails = {
			type: "urn:ietf:rfc:7231#section-6.6.1",
			title: "An Unexpected Non-Error Was Thrown",
			status: 500,
			detail: `An unexpected non-error value was thrown during the API request to ${context.url}: ${String(context.error)}`
		};
		throw new ProblemDetailsError(fallbackProblem);
	}
};
